/* global fetch, AbortController, URL, setTimeout, clearTimeout */

/**
 * POSTs a message to the AroundLink REST API as JSON.
 *
 * Every way a call can fail ends as an `ApiError` with a `kind` the caller can act on and a
 * `message` a user can read — never a raw "Failed to fetch".
 */

const TIMEOUT_MS = 15000;
const RETRY_DELAY_MS = 1500;

/**
 * On send the call sits between the user and their outgoing email, so it is kept short and tried
 * once: capture must never make someone wait, nor hold their message hostage to our backend.
 */
const SEND_TIMEOUT_MS = 8000;

export class ApiError extends Error {
  /**
   * @param {"config"|"network"|"timeout"|"auth"|"http"} kind
   * @param {string} message
   * @param {number} [status]
   */
  constructor(kind, message, status) {
    super(message);
    this.name = "ApiError";
    this.kind = kind;
    this.status = status;
  }
}

function assertUsable(config) {
  if (!config.apiUrl) {
    throw new ApiError("config", "No API address is configured (src/shared/config.js).");
  }

  let url;
  try {
    url = new URL(config.apiUrl);
  } catch {
    throw new ApiError("config", "The configured API address is not a valid URL.");
  }
  if (url.protocol !== "https:") {
    // An add-in page is served over HTTPS; the browser blocks any call to plain HTTP.
    throw new ApiError("config", "The configured API address must start with https://.");
  }
}

async function attempt(config, payload, timeoutMs = TIMEOUT_MS) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  let response;
  try {
    // A dedicated header, not `Authorization: Bearer`: the AroundLink API's JWT firewall claims
    // `Authorization` on /api/* and would reject the call before it reaches the inbound route.
    const headers = { "Content-Type": "application/json", Accept: "application/json" };
    if (config.apiToken) {
      headers["X-AroundLink-Token"] = config.apiToken;
    }

    response = await fetch(config.apiUrl, {
      method: "POST",
      headers,
      body: JSON.stringify(payload),
      signal: controller.signal,
    });
  } catch (e) {
    if (e && e.name === "AbortError") {
      throw new ApiError("timeout", "AroundLink did not answer in time.");
    }
    // fetch() rejects with a TypeError when the request never got a response: offline, DNS,
    // refused connection — or a CORS refusal, which the browser reports the same way.
    throw new ApiError("network", "AroundLink could not be reached. Check your connection.");
  } finally {
    clearTimeout(timer);
  }

  if (response.status === 401 || response.status === 403) {
    throw new ApiError("auth", "AroundLink refused the access token.", response.status);
  }
  if (!response.ok) {
    throw new ApiError(
      "http",
      `AroundLink answered with an error (${response.status}).`,
      response.status
    );
  }

  // The body is optional: a 204, or a 200 that is not JSON, is still a success.
  try {
    return await response.json();
  } catch {
    return {};
  }
}

/** Worth a second try: the request may simply have hit a blip. */
function isTransient(error) {
  return (
    error instanceof ApiError &&
    (error.kind === "network" || error.kind === "timeout" || [502, 503, 504].includes(error.status))
  );
}

/**
 * @param {{apiUrl: string, apiToken: string}} config
 * @param {object} payload
 * @returns {Promise<object>} the API's JSON answer
 * @throws {ApiError}
 */
export async function postMessage(config, payload) {
  assertUsable(config);

  try {
    return await attempt(config, payload);
  } catch (error) {
    if (!isTransient(error)) {
      throw error;
    }
    await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));

    return attempt(config, payload);
  }
}

/**
 * Like {@link postMessage}, but a single short attempt — for the send path, where a retry would
 * keep the user waiting on their own email. {@see SEND_TIMEOUT_MS}.
 *
 * @param {{apiUrl: string, apiToken: string}} config
 * @param {object} payload
 * @returns {Promise<object>} the API's JSON answer
 * @throws {ApiError}
 */
export async function postMessageQuick(config, payload) {
  assertUsable(config);

  return attempt(config, payload, SEND_TIMEOUT_MS);
}
